import React, { useState, useEffect } from 'react';
import { useParams, useOutletContext, useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stepper,
  Step,
  StepLabel,
  LinearProgress,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  TextField,
  InputAdornment
} from '@mui/material';
import { styled } from '@mui/material/styles';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import DownloadIcon from '@mui/icons-material/Download';
import DescriptionIcon from '@mui/icons-material/Description';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import Tooltip from '@mui/material/Tooltip';
import axios from 'axios';

// Use the same backend URL detection logic as api.js
const API_BASE_URL = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? window.location.protocol + '//' + window.location.hostname + ':5001'
  : window.location.protocol + '//' + window.location.hostname + '/api';

console.log('BPML Page - Using backend URL:', API_BASE_URL);

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  '& svg': {
    marginRight: theme.spacing(1),
  },
}));

const UploadBox = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(3),
  textAlign: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s',
  '&:hover': {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.hover,
  },
}));

const BPMLPage = () => {
  const { projectId } = useParams();
  const { project } = useOutletContext();
  const navigate = useNavigate();

  // State management
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);

  const [scopeItems, setScopeItems] = useState([]);
  const [totalInScope, setTotalInScope] = useState(0);
  const [scopeItemsExpanded, setScopeItemsExpanded] = useState(false);

  const [bpdTitles, setBpdTitles] = useState([]);
  const [bpmlScopeItems, setBpmlScopeItems] = useState([]);
  const [generationMode, setGenerationMode] = useState('bpd_title');
  const [showBpdTitlesDialog, setShowBpdTitlesDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const [bpdDocuments, setBpdDocuments] = useState([]);
  const [docFilter, setDocFilter] = useState('');
  const [clearAllDocDialogOpen, setClearAllDocDialogOpen] = useState(false);
  const [deletingDocId, setDeletingDocId] = useState(null);

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeStep, setActiveStep] = useState(0);

  const steps = ['Download Template', 'Fill & Upload BPML', 'Review BPD Titles', 'Generate Documents'];

  // Verify project ID matches selected project from localStorage
  useEffect(() => {
    const selectedProject = localStorage.getItem('selectedProject');
    if (selectedProject) {
      try {
        const project = JSON.parse(selectedProject);
        const selectedProjectId = project.id ? project.id.toString() : null;

        // If URL projectId doesn't match selected project, redirect to correct project
        if (selectedProjectId && projectId !== selectedProjectId) {
          console.warn(`Project mismatch detected. URL has project ${projectId} but selected project is ${selectedProjectId}. Redirecting...`);
          navigate(`/projects/${selectedProjectId}/explore/bpml-bpd`, { replace: true });
        }
      } catch (e) {
        console.error('Error parsing selected project:', e);
      }
    }
  }, [projectId, navigate]);

  // Fetch scope items and BPD documents on component mount
  useEffect(() => {
    if (projectId) {
      fetchScopeItems();
      fetchBPDDocuments();
      checkUploadStatus();
    }
  }, [projectId]);

  const checkUploadStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/explore/bpml/${projectId}/bpd-titles`);
      if (response.data && response.data.bpd_titles) {
        setActiveStep(2); // Move to step 3 if upload exists
      }
    } catch (error) {
      // No upload yet, stay at step 0
      setActiveStep(0);
    }
  };

  const fetchScopeItems = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/explore/bpml/${projectId}/scope-items`);
      setScopeItems(response.data.scope_items || []);
      setTotalInScope(response.data.total_in_scope || 0);
    } catch (error) {
      console.error('Error fetching scope items:', error);
      showSnackbar('Error loading scope items', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchBPDDocuments = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/explore/bpd/${projectId}/documents`);
      setBpdDocuments(response.data.documents || []);
      if (response.data.documents && response.data.documents.length > 0) {
        setActiveStep(4); // All steps completed
      }
    } catch (error) {
      console.error('Error fetching BPD documents:', error);
    }
  };

  const handleDownloadBPML = async () => {
    setDownloading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/explore/bpml/${projectId}/download`,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers['content-disposition'];
      let filename = `BPML_${project?.name || 'Project'}_${new Date().toISOString().split('T')[0]}.xlsx`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSnackbar('BPML template downloaded successfully!', 'success');
      setActiveStep(1); // Move to next step
    } catch (error) {
      console.error('Error downloading BPML template:', error);
      showSnackbar('Error downloading BPML template', 'error');
    } finally {
      setDownloading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUploadBPML = async () => {
    if (!selectedFile) {
      showSnackbar('Please select a file first', 'warning');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);

      const response = await axios.post(
        `${API_BASE_URL}/explore/bpml/${projectId}/upload-filled`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setBpdTitles(response.data.bpd_titles || []);
      setBpmlScopeItems(response.data.scope_items || []);
      setGenerationMode('bpd_title');
      setShowBpdTitlesDialog(true);
      showSnackbar(`Successfully parsed ${response.data.total_rows} rows with ${response.data.unique_bpd_titles} BPD titles`, 'success');
      setActiveStep(2); // Move to review step
    } catch (error) {
      console.error('Error uploading BPML file:', error);
      showSnackbar('Error uploading BPML file', 'error');
    } finally {
      setUploading(false);
    }
  };

  const handleGenerateBPDs = async () => {
    setGenerating(true);
    setShowBpdTitlesDialog(false);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/explore/bpd/${projectId}/generate`,
        { mode: generationMode }
      );
      showSnackbar(`Successfully generated ${response.data.total_documents} BPD documents`, 'success');
      fetchBPDDocuments();
      setActiveStep(4);
    } catch (error) {
      console.error('Error generating BPD documents:', error);
      showSnackbar('Error generating BPD documents', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownloadBPD = async (bpdId, filename) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/explore/bpd/${projectId}/download/${bpdId}`,
        { responseType: 'blob' }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showSnackbar('BPD document downloaded successfully!', 'success');
    } catch (error) {
      console.error('Error downloading BPD document:', error);
      showSnackbar('Error downloading BPD document', 'error');
    }
  };

  const handleDeleteBPDDocument = async (docId) => {
    setDeletingDocId(docId);
    try {
      await axios.delete(`${API_BASE_URL}/explore/bpd/${projectId}/documents/${docId}`);
      setBpdDocuments(prev => prev.filter(d => d.id !== docId));
      showSnackbar('Document deleted');
    } catch (error) {
      showSnackbar('Error deleting document', 'error');
    } finally {
      setDeletingDocId(null);
    }
  };

  const handleClearAllBPDDocuments = async () => {
    setClearAllDocDialogOpen(false);
    try {
      await axios.delete(`${API_BASE_URL}/explore/bpd/${projectId}/documents/clear-all`);
      setBpdDocuments([]);
      setDocFilter('');
      showSnackbar('All BPD documents cleared');
    } catch (error) {
      showSnackbar('Error clearing BPD documents', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  if (loading && scopeItems.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <SectionTitle variant="h4">
            <AccountTreeIcon />
            BPML & BPD
          </SectionTitle>
          <Chip
            icon={<CheckCircleIcon />}
            label={`${totalInScope} Items In Scope`}
            color="success"
            variant="outlined"
          />
        </Box>

        <Typography variant="body1" color="text.secondary" paragraph>
          Business Process Master List and Business Process Design for {project?.name}
        </Typography>

        {/* Stepper */}
        <Stepper activeStep={activeStep} alternativeLabel sx={{ mt: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Scope Items Accordion */}
      <Accordion
        expanded={scopeItemsExpanded}
        onChange={(e, isExpanded) => setScopeItemsExpanded(isExpanded)}
        sx={{ mb: 3 }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box display="flex" alignItems="center" width="100%">
            <InfoIcon sx={{ mr: 1, color: '#2196F3' }} />
            <Typography variant="h6">
              Scope Items Marked for Workshops ({totalInScope})
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="body2" color="text.secondary" paragraph>
            The following scope items will be marked as "Yes" in the BPML template.
          </Typography>

          {scopeItems.length > 0 ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Module</strong></TableCell>
                    <TableCell><strong>Scope Item</strong></TableCell>
                    <TableCell><strong>Description</strong></TableCell>
                    <TableCell align="center"><strong>Status</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scopeItems.map((item, index) => (
                    <TableRow key={item.id || index}>
                      <TableCell>
                        <Chip label={item.module} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>{item.scope_item}</TableCell>
                      <TableCell>{item.scope_item_description || 'N/A'}</TableCell>
                      <TableCell align="center">
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="In Scope"
                          color="success"
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">
              No scope items marked for workshops yet.
            </Alert>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Step 1: Download BPML Template */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Box display="flex" alignItems="center" mb={2}>
                <DescriptionIcon sx={{ mr: 1, color: '#4DB6AC' }} />
                <Typography variant="h6">Step 1: Download BPML Template</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph sx={{ flexGrow: 1 }}>
                Download the customized BPML template with scope items pre-marked. Fill in columns:
                BPML Master ID, BPD Title, Level 4, and Include initial.
              </Typography>
              <Button
                variant="contained"
                startIcon={downloading ? <CircularProgress size={20} /> : <DownloadIcon />}
                onClick={handleDownloadBPML}
                disabled={downloading}
                sx={{ backgroundColor: '#4DB6AC', '&:hover': { backgroundColor: '#3d9b91' }, mt: 'auto' }}
                fullWidth
              >
                {downloading ? 'Downloading...' : 'Download BPML Template'}
              </Button>
            </CardContent>
          </Card>
        </Grid>

        {/* Step 2: Upload Filled BPML */}
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
              <Box display="flex" alignItems="center" mb={2}>
                <CloudUploadIcon sx={{ mr: 1, color: '#FF9800' }} />
                <Typography variant="h6">Step 2: Upload Filled BPML</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" paragraph>
                After filling the BPML template, upload it here to extract BPD titles.
              </Typography>

              <input
                accept=".xlsx,.xls"
                style={{ display: 'none' }}
                id="bpml-file-upload"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="bpml-file-upload" style={{ flexGrow: 1, display: 'flex', alignItems: 'center' }}>
                <UploadBox component="span" sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                  <UploadFileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body2">
                    {selectedFile ? selectedFile.name : 'Click to select BPML file'}
                  </Typography>
                </UploadBox>
              </label>

              {selectedFile && (
                <Button
                  variant="contained"
                  startIcon={uploading ? <CircularProgress size={20} /> : <UploadFileIcon />}
                  onClick={handleUploadBPML}
                  disabled={uploading}
                  fullWidth
                  sx={{ mt: 2, backgroundColor: '#FF9800', '&:hover': { backgroundColor: '#F57C00' } }}
                >
                  {uploading ? 'Uploading...' : 'Upload & Parse BPML'}
                </Button>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Generated BPD Documents */}
      {bpdDocuments.length > 0 && (() => {
        const filterLower = docFilter.toLowerCase();
        const filtered = bpdDocuments.filter(d =>
          !filterLower || d.bpd_title.toLowerCase().includes(filterLower)
        );
        const byTitle = filtered.filter(d => (d.doc_type || 'bpd_title') === 'bpd_title');
        const byScopeItem = filtered.filter(d => d.doc_type === 'scope_item');
        const hasBoth = byTitle.length > 0 && byScopeItem.length > 0;

        const DocCard = ({ doc }) => (
          <Grid item xs={12} sm={6} md={4}>
            <Card variant="outlined">
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, flexGrow: 1, mr: 1 }}>
                    {doc.bpd_title}
                  </Typography>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Chip
                      label={doc.doc_type === 'scope_item' ? 'Scope Item' : 'BPD Title'}
                      size="small"
                      color={doc.doc_type === 'scope_item' ? 'secondary' : 'primary'}
                      variant="outlined"
                    />
                    <Tooltip title="Delete document">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteBPDDocument(doc.id)}
                        disabled={deletingDocId === doc.id}
                      >
                        {deletingDocId === doc.id
                          ? <CircularProgress size={16} />
                          : <DeleteIcon fontSize="small" />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {doc.total_requirements} requirements
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={() => handleDownloadBPD(doc.id, doc.file_name)}
                  fullWidth
                  size="small"
                >
                  Download
                </Button>
              </CardContent>
            </Card>
          </Grid>
        );

        return (
          <Paper sx={{ p: 3, mb: 3 }}>
            {/* Header + filter */}
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={2} flexWrap="wrap" gap={2}>
              <Box display="flex" alignItems="center" gap={1}>
                <ListAltIcon sx={{ mr: 1, color: '#4CAF50' }} />
                <Typography variant="h6">
                  Generated BPD Documents ({filtered.length}{filtered.length !== bpdDocuments.length ? ` of ${bpdDocuments.length}` : ''})
                </Typography>
                <Button
                  size="small"
                  color="error"
                  variant="outlined"
                  startIcon={<DeleteIcon />}
                  onClick={() => setClearAllDocDialogOpen(true)}
                >
                  Clear All
                </Button>
              </Box>
              <TextField
                size="small"
                placeholder="Filter by title or scope item..."
                value={docFilter}
                onChange={(e) => setDocFilter(e.target.value)}
                sx={{ minWidth: 260 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
            </Box>

            {filtered.length === 0 ? (
              <Alert severity="info">No documents match your filter.</Alert>
            ) : (
              <>
                {/* BPD Title segment */}
                {byTitle.length > 0 && (
                  <Box mb={hasBoth ? 3 : 0}>
                    {hasBoth && (
                      <Box display="flex" alignItems="center" mb={1.5}>
                        <Chip label="By BPD Title" color="primary" size="small" sx={{ mr: 1 }} />
                        <Typography variant="body2" color="text.secondary">{byTitle.length} document{byTitle.length !== 1 ? 's' : ''}</Typography>
                      </Box>
                    )}
                    <Grid container spacing={2}>
                      {byTitle.map(doc => <DocCard key={doc.id} doc={doc} />)}
                    </Grid>
                  </Box>
                )}

                {hasBoth && <Divider sx={{ my: 2 }} />}

                {/* Scope Item segment */}
                {byScopeItem.length > 0 && (
                  <Box>
                    {hasBoth && (
                      <Box display="flex" alignItems="center" mb={1.5}>
                        <Chip label="By Scope Item" color="secondary" size="small" sx={{ mr: 1 }} />
                        <Typography variant="body2" color="text.secondary">{byScopeItem.length} document{byScopeItem.length !== 1 ? 's' : ''}</Typography>
                      </Box>
                    )}
                    <Grid container spacing={2}>
                      {byScopeItem.map(doc => <DocCard key={doc.id} doc={doc} />)}
                    </Grid>
                  </Box>
                )}
              </>
            )}
          </Paper>
        );
      })()}

      {/* BPD Generation Dialog */}
      <Dialog
        open={showBpdTitlesDialog}
        onClose={() => setShowBpdTitlesDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <AutoAwesomeIcon sx={{ mr: 1, color: '#4CAF50' }} />
            Generate BPD Documents
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 3 }}>
            Parsed {bpdTitles.length} unique BPD title{bpdTitles.length !== 1 ? 's' : ''} and {bpmlScopeItems.length} unique scope item{bpmlScopeItems.length !== 1 ? 's' : ''} from your BPML file.
          </Alert>

          <FormControl component="fieldset" sx={{ mb: 3 }}>
            <FormLabel component="legend" sx={{ fontWeight: 600, mb: 1 }}>Generate By</FormLabel>
            <RadioGroup
              row
              value={generationMode}
              onChange={(e) => setGenerationMode(e.target.value)}
            >
              <FormControlLabel
                value="bpd_title"
                control={<Radio />}
                label={`BPD Title (${bpdTitles.length})`}
              />
              <FormControlLabel
                value="scope_item"
                control={<Radio />}
                label={`Scope Item (${bpmlScopeItems.length})`}
              />
              <FormControlLabel
                value="both"
                control={<Radio />}
                label={`Both (${bpdTitles.length + bpmlScopeItems.length} documents)`}
              />
            </RadioGroup>
          </FormControl>

          {(generationMode === 'bpd_title' || generationMode === 'both') && (
            <Box sx={{ mb: generationMode === 'both' ? 2 : 0 }}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                BPD Title Documents ({bpdTitles.length})
              </Typography>
              <List dense sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {bpdTitles.map((title, index) => (
                  <ListItem key={index}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <DescriptionIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={title}
                      secondary={`${project?.name}-${title}.docx`}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          {(generationMode === 'scope_item' || generationMode === 'both') && (
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                Scope Item Documents ({bpmlScopeItems.length})
              </Typography>
              <List dense sx={{ maxHeight: 200, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                {bpmlScopeItems.map((code, index) => (
                  <ListItem key={index}>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <AccountTreeIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={code}
                      secondary={`${project?.name}-${code}.docx`}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowBpdTitlesDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={generating ? <CircularProgress size={20} /> : <AutoAwesomeIcon />}
            onClick={handleGenerateBPDs}
            disabled={generating}
          >
            {generating ? 'Generating...' : 'Generate BPD Documents'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Loading Overlay */}
      {generating && (
        <Paper sx={{ p: 3, position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 9999 }}>
          <Box textAlign="center">
            <CircularProgress size={60} sx={{ mb: 2 }} />
            <Typography variant="h6">Generating BPD Documents...</Typography>
            <Typography variant="body2" color="text.secondary">
              Using AI to create business requirements. This may take a few minutes.
            </Typography>
            <LinearProgress sx={{ mt: 2 }} />
          </Box>
        </Paper>
      )}

      {/* Clear All BPD Documents Dialog */}
      <Dialog open={clearAllDocDialogOpen} onClose={() => setClearAllDocDialogOpen(false)}>
        <DialogTitle>Clear All BPD Documents?</DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This will permanently delete all {bpdDocuments.length} generated BPD document{bpdDocuments.length !== 1 ? 's' : ''} for this project.
          </Alert>
          <Typography>
            The document files and records will be removed. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearAllDocDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleClearAllBPDDocuments}>
            Yes, Clear All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BPMLPage;
